import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import Logo from "../../components/shared/Logo";
import { useAuth } from "../../context/AuthContext";

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success("Welcome back!", { description: `Logged in as ${user.full_name}` });
      
      // Redirect based on role
      if (user.role === "super_admin") {
        navigate("/admin/dashboard");
      } else if (user.role === "tenant_admin") {
        navigate("/tenant/dashboard");
      } else if (user.role === "manager") {
        navigate("/manager/dashboard");
      } else {
        navigate("/learner/dashboard");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Invalid email or password";
      toast.error("Login failed", { description: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen tts-navy-bg relative overflow-hidden flex items-center justify-center p-4">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating Circles */}
        <div className="absolute top-20 left-20 w-32 h-32 rounded-full bg-white/5 animate-pulse" />
        <div className="absolute bottom-40 right-20 w-48 h-48 rounded-full bg-white/3" />
        <div className="absolute top-1/3 right-32 w-24 h-24 border border-white/10 rotate-45" />
        
        {/* Dotted Pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F1923] via-[#1A2B3C] to-[#0F1923]" />
      </div>

      {/* Form Container */}
      <div className="w-full max-w-md relative z-10 animate-fade-in">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="bg-background/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-2xl border border-white/10">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Logo size="lg" variant="light" />
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
              Welcome back
            </h1>
            <p className="text-muted-foreground text-sm">
              Sign in to continue training
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11 bg-background/50 border-border/50 focus:border-primary"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/auth/forgot-password"
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 bg-background/50 border-border/50 focus:border-primary"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11 text-base font-semibold btn-primary"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-4 p-3 rounded-lg bg-muted/50 border border-border/50">
            <p className="text-xs text-muted-foreground text-center mb-2 font-medium">Quick Login (Demo Accounts):</p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button 
                type="button"
                onClick={() => { setEmail('admin@ttslearnhub.com'); setPassword('Admin123!'); }}
                className="p-2 rounded bg-background hover:bg-muted text-left border border-border/50 transition-colors"
              >
                <span className="font-medium text-foreground">Super Admin</span>
                <span className="block text-muted-foreground truncate">admin@ttslearnhub.com</span>
              </button>
              <button 
                type="button"
                onClick={() => { setEmail('sarah@acme.com'); setPassword('Demo123!'); }}
                className="p-2 rounded bg-background hover:bg-muted text-left border border-border/50 transition-colors"
              >
                <span className="font-medium text-foreground">Business Admin</span>
                <span className="block text-muted-foreground truncate">sarah@acme.com</span>
              </button>
              <button 
                type="button"
                onClick={() => { setEmail('james@acme.com'); setPassword('Demo123!'); }}
                className="p-2 rounded bg-background hover:bg-muted text-left border border-border/50 transition-colors"
              >
                <span className="font-medium text-foreground">Learner</span>
                <span className="block text-muted-foreground truncate">james@acme.com</span>
              </button>
              <button 
                type="button"
                onClick={() => { setEmail('mike@acme.com'); setPassword('Demo123!'); }}
                className="p-2 rounded bg-background hover:bg-muted text-left border border-border/50 transition-colors"
              >
                <span className="font-medium text-foreground">Manager</span>
                <span className="block text-muted-foreground truncate">mike@acme.com</span>
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/50" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-4 bg-background text-muted-foreground">or continue with</span>
            </div>
          </div>

          {/* Social Login */}
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              className="h-10 bg-background/50 text-sm"
              onClick={() => {
                const redirectUrl = window.location.origin;
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="login-google-btn"
            >
              <img src="/logo-google.webp" alt="Google" className="w-4 h-4 mr-2 object-contain" />
              Google
            </Button>
            <Button variant="outline" className="h-10 bg-background/50 text-sm" disabled data-testid="login-microsoft-btn">
              <img src="/logo-microsoft.png" alt="Microsoft" className="w-4 h-4 mr-2 object-contain" />
              Microsoft
            </Button>
          </div>

          {/* Sign Up Link */}
          <p className="text-center mt-5 text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/auth/signup" className="text-primary font-medium hover:text-primary/80 transition-colors">
              Sign up free
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
